import React from "react";
import { useTranslation } from "react-i18next";
import { GearPal } from "../types/Pal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteGearpal } from "../backend/api/gearlpal";
import toast from "react-hot-toast";
import { useUserStore } from "../stores/UserStore";
import GearHeadings from "../ui/gearheadings";
import {
  IoClose,
  IoChevronForward,
  IoLocationOutline,
  IoCubeOutline,
} from "react-icons/io5";

interface GearPalClusterPostalCodePopupProps {
  zipCode: string;
  gearPals: GearPal[];
  onClose: () => void;
  onSelectPal: (pal: GearPal, showBackButton?: boolean) => void;
  onPalUpdate: () => void;
}

const GearPalClusterPostalCodePopup: React.FC<
  GearPalClusterPostalCodePopupProps
> = ({ zipCode, gearPals, onClose, onSelectPal, onPalUpdate }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useUserStore();

  // Mutation per eliminare GearPal
  const { mutate: deleteGearpalMutation } = useMutation({
    mutationFn: (id: number) => deleteGearpal(id, i18n.language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gearpals"] });
      queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });
      toast.success(
        t("pal.gearDeleted", "Attrezzatura eliminata con successo")
      );
      onPalUpdate();
    },
    onError: () => {
      toast.error(t("pal.deleteFailed", "Errore durante l'eliminazione"));
    },
  });

  const handleDeleteGearPal = (pal: GearPal, e: React.MouseEvent) => {
    e.stopPropagation();
    if (
      window.confirm(
        t(
          "forms.confirmDelete",
          "Sei sicuro di voler eliminare questo elemento?"
        )
      )
    ) {
      deleteGearpalMutation(pal.id!);
    }
  };

  const handleSelectPal = (pal: GearPal) => {
    onSelectPal(pal, true);
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[1002] transition-all duration-300"
      onClick={handleOverlayClick}
    >
      <div className="bg-[#2D1B4EE5] border-modal rounded-[25px] shadow-[0_30px_60px_rgba(3,0,28,0.55)] max-w-lg w-full overflow-hidden flex flex-col max-h-[85vh] animate-scale-in">
        {/* Header */}
        <div className="relative p-7 pb-5 border-b border-white/5">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
              <IoLocationOutline size={22} />
            </div>
            <GearHeadings variant="h3" className="text-white !font-bold">
              {zipCode}
            </GearHeadings>
          </div>
          <p className="text-white/50 text-sm ml-13">
            {t("map.gearProvidersIn", "Gear providers in this area")}
          </p>

          <button
            onClick={onClose}
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-all border border-white/5"
          >
            <IoClose size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto custom-scrollbar flex-grow">
          <div className="space-y-3">
            {gearPals.map((pal) => {
              const isOwner =
                user &&
                user.email.toLowerCase() === pal.userEmail.toLowerCase();

              return (
                <div
                  key={pal.userEmail}
                  className="group relative px-4 py-2 bg-[#3A1A60] border border-white/5 rounded-[14px] flex justify-between items-center cursor-pointer hover:bg-white/10 hover:border-secondary/30 transition-all duration-200"
                  onClick={() => handleSelectPal(pal)}
                >
                  <div className="flex-1 min-w-0 pr-4">
                    <h3 className="text-secondary font-semibold text-base truncate mb-1 flex items-center gap-2">
                      {pal.name}
                      {isOwner && (
                        <span className="text-[10px] bg-primary/20 text-primary-300 px-2 py-0.5 rounded-full border border-primary/30 uppercase tracking-wider">
                          {t("common.you", "Tu")}
                        </span>
                      )}
                    </h3>
                    <div className="flex items-center gap-3 text-white/40 text-sm">
                      <div className="flex items-center gap-1.5">
                        <IoCubeOutline
                          size={14}
                          className="text-secondary/60"
                        />
                        <span>
                          {pal.items.length}{" "}
                          {pal.items.length === 1
                            ? t("map.item", "oggetto")
                            : t("map.items", "oggetti")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-secondary group-hover:text-secondary group-hover:bg-secondary/10 transition-all">
                      <IoChevronForward size={18} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {gearPals.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center text-white/10 mb-4">
                <IoCubeOutline size={32} />
              </div>
              <p className="text-white/40 text-sm max-w-[200px]">
                {t(
                  "map.noGearPalsFound",
                  "Nessun fornitore trovato in questa zona"
                )}
              </p>
            </div>
          )}
        </div>

        {/* Footer info/actions could go here */}
        <div className="p-4 bg-black/20 border-t border-white/5">
          <p className="text-[11px] text-white/30 text-center uppercase tracking-[0.1em]">
            GearSurfing Network • {gearPals.length}{" "}
            {t("common.results", "Risultati")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default GearPalClusterPostalCodePopup;
